import type { MapPoint, MapPointCategory } from '@/components/map/map.types';

export interface PointCollection {
  category: MapPointCategory;
  geoJson: {
    type: 'FeatureCollection';
    features: any[];
  };
}

export function pointSourceId(categoryId: number | string) {
  return `points_${categoryId}`;
}

export function pointClusterLayerId(categoryId: number | string) {
  return `clusters_${categoryId}`;
}

export function pointClusterCountLayerId(categoryId: number | string) {
  return `cluster-count_${categoryId}`;
}

export function unclusteredPointLayerId(categoryId: number | string) {
  return `unclustered-point_${categoryId}`;
}

export function categoryMarkerImageId(color: string) {
  return `marker_${color}`;
}

export const MARKER_ICON_BASE_URL = 'https://s3.ca-central-1.amazonaws.com/cocoriko.dev.communities/mapMarkers/png';

export function categoryMarkerImageUrl(color: string) {
  return `${MARKER_ICON_BASE_URL}/mapbox_marker_${color}.png`;
}

export function categoryMarkerIconUrl(color: string) {
  return `${categoryMarkerImageUrl(color)}?icon=1`;
}

function toFeatureProperty(value: unknown) {
  if (value == null) return value;
  return typeof value === 'object' ? JSON.stringify(value) : value;
}

function toFeatureProperties(payload: Record<string, unknown>) {
  return Object.fromEntries(Object.entries(payload).map(([key, value]) => [key, toFeatureProperty(value)]));
}

export function createPointGeoJson(categories: MapPointCategory[], points: MapPoint[]): PointCollection[] {
  return (categories || []).map((category) => ({
    category,
    geoJson: {
      type: 'FeatureCollection' as const,
      features: (points || [])
        .filter((point) => point.category === category.id)
        .map((point) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [point.longitude, point.latitude],
          },
          properties: {
            ...toFeatureProperties(point.payload ?? {}),
            pointId: point.id,
            title: point.label ?? null,
            markerCategoryId: category.id,
            markerColor: category.color,
            markerSize: 'medium',
          },
        })),
    },
  }));
}

export function loadCategoryMarkerImages(map: any, categories: MapPointCategory[]) {
  (categories || []).forEach((category) => {
    const imageId = categoryMarkerImageId(category.color);
    map.loadImage(categoryMarkerIconUrl(category.color), (error: unknown, image: unknown) => {
      // Throwing here would escape into Mapbox's callback with nobody to catch it; one missing
      // icon should cost that category its marker, not the whole map.
      if (error) {
        console.error(`Could not load the marker icon for category colour "${category.color}"`, error);
        return;
      }
      if (!map.hasImage(imageId)) {
        map.addImage(imageId, image);
      }
    });
  });
}

export function addCategoryPointLayers(
  map: any,
  collections: PointCollection[],
  options: { clusterEnabled?: boolean; onPointClick?: (features: any) => void } = {},
): string[] {
  const clusterEnabled = options.clusterEnabled === true;
  const interactiveLayerIds: string[] = [];

  collections.forEach((collection) => {
    const categoryId = collection.category.id;
    const sourceId = pointSourceId(categoryId);
    const unclusteredId = unclusteredPointLayerId(categoryId);
    interactiveLayerIds.push(unclusteredId);

    map.addSource(sourceId, {
      type: 'geojson',
      data: collection.geoJson,
      cluster: clusterEnabled,
      clusterMaxZoom: 14,
      clusterRadius: 20,
    });

    if (clusterEnabled) {
      map.addLayer({
        id: pointClusterLayerId(categoryId),
        type: 'circle',
        source: sourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': ['step', ['get', 'point_count'], '#51bbd6', 100, '#f1f075', 750, '#f28cb1'],
          'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
        },
        layout: {
          visibility: 'visible',
        },
      });
      map.addLayer({
        id: pointClusterCountLayerId(categoryId),
        type: 'symbol',
        source: sourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
          visibility: 'visible',
        },
      });
    }

    map.addLayer({
      id: unclusteredId,
      type: 'symbol',
      source: sourceId,
      filter: ['!', ['has', 'point_count']],
      layout: {
        visibility: 'visible',
        'icon-image': categoryMarkerImageId(collection.category.color),
        'icon-size': 1,
        'icon-allow-overlap': true,
      },
    });

    if (options.onPointClick) {
      map.on('click', unclusteredId, (e: any) => {
        options.onPointClick!(e.features);
      });
    }
  });

  return interactiveLayerIds;
}

export function bottomPointLayerId(map: any, collections: PointCollection[]): string | undefined {
  for (const collection of collections) {
    const categoryId = collection.category.id;
    const candidates = [
      pointClusterLayerId(categoryId),
      pointClusterCountLayerId(categoryId),
      unclusteredPointLayerId(categoryId),
    ];
    for (const id of candidates) {
      if (map?.getLayer(id)) return id;
    }
  }
  return undefined;
}

export function setPointCollectionData(map: any, collection: PointCollection) {
  const source = map?.getSource(pointSourceId(collection.category.id));
  if (source && source.setData) {
    source.setData(collection.geoJson);
  }
}

export function setCategoryPointsVisibility(map: any, categoryId: number | string, visible: boolean) {
  const layerId = unclusteredPointLayerId(categoryId);
  if (map?.getLayer(layerId)) {
    map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
  }
}
